import React from "react";
import { motion } from "framer-motion";
import { CalendarCheck2, ArrowRight } from "lucide-react";
import { useContentLibrary } from "@/data/content";

const FOUNDER_PHOTO = `${process.env.PUBLIC_URL || ""}/assets/Sanjay%20Melinamani%202026%20-%202.png`;

// To activate live scheduling, replace with the founder's Calendly URL
// (e.g. https://calendly.com/sanjay-jaisphere/architecture-review).
// Falls back to a pre-filled mailto if left empty.
const BOOKING_URL = "https://calendly.com/jaisphere/30min";
const BOOKING_FALLBACK =
  "mailto:jaisphere@gmail.com?subject=Architecture%20review%20with%20Sanjay&body=Hi%20Sanjay%2C%0A%0AI%27d%20like%20to%20schedule%20a%2030-minute%20architecture%20review.%0A%0AContext%3A%20%5Bbriefly%20describe%20your%20goals%5D%0APreferred%20times%3A%20%5Bshare%202-3%20windows%5D%0A%0AThanks";
const bookingHref = BOOKING_URL || BOOKING_FALLBACK;

export const About = () => {
  const { data } = useContentLibrary();
  const { eyebrow, title, story, stats, credentials } = data.about;

  return (
    <section id="about" className="relative py-24 md:py-32 bg-white dark:bg-zinc-950">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12 grid grid-cols-12 gap-10 items-start">
        {/* Founder portrait card */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="col-span-12 lg:col-span-5"
        >
          <div className="relative">
            <div className="relative overflow-hidden rounded-sm border border-zinc-200 dark:border-zinc-800 bg-zinc-900">
              <img
                src={FOUNDER_PHOTO}
                alt="Sanjay Melinamani, Founder of Jaisphere"
                data-testid="founder-photo"
                className="w-full h-[560px] object-cover object-top"
              />
              <div className="absolute bottom-0 inset-x-0 p-6 bg-gradient-to-t from-zinc-950 via-zinc-950/70 to-transparent">
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-300">
                  Founder & Principal Architect
                </div>
                <div className="mt-1 font-display font-black tracking-tight text-2xl text-white">
                  Sanjay Melinamani
                </div>
              </div>
            </div>
            <div className="absolute -bottom-5 -right-5 bg-[#002FA7] text-white px-5 py-3 rounded-sm shadow-lg">
              <div className="font-mono text-[10px] uppercase tracking-widest opacity-80">
                Headquarters
              </div>
              <div className="font-display font-bold tracking-tight">Atlanta, Georgia</div>
            </div>
          </div>
        </motion.div>

        {/* Story */}
        <div className="col-span-12 lg:col-span-7">
          <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#002FA7] dark:text-[#7C9BFF]">
            {eyebrow}
          </span>
          <h2 className="mt-4 font-display font-black tracking-tighter text-zinc-950 dark:text-zinc-50 text-4xl sm:text-5xl leading-[1.02]">
            {title.map((line, index) => (
              <React.Fragment key={line}>
                {index > 0 && <br />}
                {line}
              </React.Fragment>
            ))}
          </h2>
          {story.map((block, index) => (
            <p
              key={block.text}
              className={`${index === 0 ? "mt-6" : "mt-4"} text-zinc-600 dark:text-zinc-400 leading-relaxed text-base`}
            >
              {block.text}
            </p>
          ))}

          <div className="mt-10 grid grid-cols-2 gap-6 border-t border-zinc-200 dark:border-zinc-800 pt-8">
            {stats.map((s) => (
              <div key={s.v}>
                <div className="font-display font-black tracking-tighter text-3xl sm:text-4xl text-zinc-950 dark:text-zinc-50">
                  {s.k}
                </div>
                <div className="mt-1 text-xs font-mono uppercase tracking-widest text-zinc-500 dark:text-zinc-400">
                  {s.v}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10">
            <div className="text-xs font-bold uppercase tracking-[0.22em] text-zinc-500 dark:text-zinc-400">
              Certifications & Credentials
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {credentials.map((c) => (
                <span
                  key={c}
                  className="font-mono text-[11px] uppercase tracking-wider px-2.5 py-1.5 border border-zinc-200 bg-zinc-50 text-zinc-700 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300"
                >
                  {c}
                </span>
              ))}
            </div>
          </div>

          {/* Booking CTA */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6 }}
            data-testid="founder-booking-card"
            className="mt-10 relative overflow-hidden bg-zinc-950 text-white rounded-sm p-7 md:p-8 border border-zinc-800"
          >
            <div
              aria-hidden
              className="absolute inset-0 opacity-[0.10] pointer-events-none"
              style={{
                backgroundImage:
                  "linear-gradient(to right, rgba(255,255,255,0.18) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.18) 1px, transparent 1px)",
                backgroundSize: "32px 32px",
              }}
            />
            <div className="relative flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-6">
              <span className="inline-flex items-center justify-center h-12 w-12 bg-[#002FA7] rounded-sm shrink-0">
                <CalendarCheck2 size={22} />
              </span>
              <div className="flex-1">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#7C9BFF]">
                  Founder-led · 30 minutes · No slideware
                </div>
                <div className="mt-1 font-display font-bold tracking-tight text-xl sm:text-2xl">
                  Schedule a 30-min architecture review with Sanjay.
                </div>
                <p className="mt-1 text-sm text-zinc-300 max-w-md">
                  Walk through your AI governance, cloud migration, or modernization roadmap with the founder directly.
                </p>
              </div>
              <a
                href={bookingHref}
                target={BOOKING_URL ? "_blank" : undefined}
                rel={BOOKING_URL ? "noopener noreferrer" : undefined}
                data-testid="booking-cta"
                className="shrink-0 inline-flex items-center gap-2 bg-white text-zinc-950 hover:bg-zinc-200 px-5 py-3 rounded-sm text-sm font-semibold transition-colors"
              >
                Book the review
                <ArrowRight size={15} />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
